/**
 * 業務フローの読取の IF — 業務フロー 1 本・ステージの図・案件が変えた業務フローの一覧。
 *
 * 形の正は設計書 api §4（`getFlow` / `listPjFlows`）。設計書に欄の形が無いもの（ステージの図の
 * 支援フローの札・`roleCheck`・`listPjFlows` の行）はここで決め、PR の「設計書に書き足す候補」に
 * 並べる。
 *
 * **ID はすべて number である。** 案件番号も number で、画面が `AIRCLOSET-` を前に付けて
 * 6 桁で出す。`nodeId` だけは業務フローの中で 1 から振る局所 ID である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー（型・入口・出口・節点・枝）とステージの図（主フロー ＋ 支援フローの札）、案件が変えた業務フローの一覧の入出力スキーマ
 * @graph-connects none
 */
import { FLOW_DESTINATION_KINDS, FLOW_ENTRY_VIEW_KINDS, FLOW_EXIT_VIEW_KINDS, FLOW_NODE_KINDS, FLOW_OUTBOUND_EFFECTS, FLOW_TYPES, STORY_FLOW_PJ_SIDES, } from '@cortex/domain-spec-graph';
import { z } from 'zod';
import { ConstraintViolationSchema, LocaleInputShape, NodeUpdatedAtSchema, UntranslatedSchema, } from '../spec-graph-common.js';
/** 1 以上の整数の ID。 @graph-connects none */
const positiveId = () => z.number().int().min(1);
/**
 * 案件の指定。**案件番号は number である。**
 *
 * `before` は「その案件を当てる直前」＝ master、`after` は「その案件の未確定変更を重ねた姿」
 * である。
 * @graph-connects none
 */
export const ElementPjSideSchema = z.object({
    number: positiveId(),
    /*
     * 面の 2 値は読取の側（`@cortex/domain-spec-graph` の `STORY_FLOW_PJ_SIDES`）が正である。
     * ここで書き直すと、面を足したときに片側だけが受け取って黙ってすり抜ける。
     */
    side: z.enum(STORY_FLOW_PJ_SIDES),
});
/**
 * 図に出る参照 1 件（業務・アクター・用語・ステージ）。
 *
 * 名前の材料が欠けていると表示名は空文字になる。**未翻訳を原文で埋めない**ので、空文字は
 * 「この言語では読めない」を意味する。
 * @graph-connects none
 */
export const FlowRefSchema = z.object({
    id: positiveId(),
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/**
 * `getFlow` の入力。`flowId`（業務フロー 1 本）と `stageId`（ステージの図）のどちらか 1 つ。
 * @graph-connects none
 */
export const GetFlowInputSchema = z
    .object({
    flowId: positiveId().optional(),
    stageId: positiveId().optional(),
    pj: ElementPjSideSchema.optional(),
    ...LocaleInputShape,
})
    .refine((input) => (input.flowId === undefined) !== (input.stageId === undefined), {
    message: 'flowId と stageId のどちらか 1 つを渡す',
});
/**
 * 業務フローの入口（参照はラベル解決済み。種別に無い参照は null）。
 * @graph-connects none
 */
export const FlowEntryViewSchema = z.object({
    kind: z.enum(FLOW_ENTRY_VIEW_KINDS),
    business: FlowRefSchema.nullable(),
    ending: FlowRefSchema.nullable(),
    term: FlowRefSchema.nullable(),
    actor: FlowRefSchema.nullable(),
});
/**
 * 結末の行 1 行（業務と、その結末の結果の値）。
 * @graph-connects none
 */
export const FlowEndingRowViewSchema = z.object({
    business: FlowRefSchema,
    ending: FlowRefSchema,
});
/**
 * 業務フローの出口（参照はラベル解決済み。種別に無い参照は null）。
 *
 * `endings` は支援 3 型の「出口に着く結末の行」で、主フローは空。`nextStages` はステージの出口
 * （主フロー）の結末の行で続く次のステージ（鎖の本線）で、ほかの種別は空。
 * @graph-connects none
 */
export const FlowExitViewSchema = z.object({
    kind: z.enum(FLOW_EXIT_VIEW_KINDS),
    business: FlowRefSchema.nullable(),
    ending: FlowRefSchema.nullable(),
    stage: FlowRefSchema.nullable(),
    counterpart: FlowRefSchema.nullable(),
    effect: z.enum(FLOW_OUTBOUND_EFFECTS).nullable(),
    endings: z.array(FlowEndingRowViewSchema),
    nextStages: z.array(FlowRefSchema),
});
/**
 * 業務フローの節点 1 個。`business` と `actor` は業務の節点だけが持つ。
 * @graph-connects none
 */
export const FlowNodeViewSchema = z.object({
    nodeId: positiveId(),
    kind: z.enum(FLOW_NODE_KINDS),
    business: FlowRefSchema.nullable(),
    actor: FlowRefSchema.nullable(),
});
/**
 * 業務フローの枝 1 本。`to` は宛先が `stop` / `exit` のとき null になりうる。`label` は前の業務の
 * 結末の結果の値で、結末を持たない枝は空文字。
 * @graph-connects none
 */
export const FlowTransitionViewSchema = z.object({
    id: z.string().min(1),
    from: positiveId(),
    to: positiveId().nullable(),
    destinationKind: z.enum(FLOW_DESTINATION_KINDS),
    endingId: positiveId().nullable(),
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/**
 * 業務フローのレーン 1 本（節点の業務のアクターを初出順に並べた導出値）。
 * @graph-connects none
 */
export const FlowLaneViewSchema = z.object({
    actorId: positiveId(),
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/**
 * 宣言した型と値の流れの整合（制約 63 の結果）。`reasons` はその違反の文。
 * @graph-connects none
 */
export const FlowRoleCheckSchema = z.object({
    declared: z.enum(FLOW_TYPES),
    consistent: z.boolean(),
    reasons: z.array(z.string()),
});
/**
 * 業務フロー 1 本。`situation` は主フローでは null。
 * @graph-connects none
 */
export const FlowViewSchema = z.object({
    flowId: positiveId(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    updatedAt: NodeUpdatedAtSchema,
    type: z.enum(FLOW_TYPES),
    stage: FlowRefSchema.nullable(),
    situation: FlowRefSchema.nullable(),
    entry: FlowEntryViewSchema,
    exit: FlowExitViewSchema,
    lanes: z.array(FlowLaneViewSchema),
    nodes: z.array(FlowNodeViewSchema),
    transitions: z.array(FlowTransitionViewSchema),
    roleCheck: FlowRoleCheckSchema,
    violations: z.array(ConstraintViolationSchema),
});
/**
 * ステージの図に並ぶ支援フローの札 1 枚（型・状況・入口・出口）。
 * @graph-connects none
 */
export const SupportFlowCardSchema = z.object({
    flowId: positiveId(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    type: z.enum(FLOW_TYPES),
    situation: FlowRefSchema.nullable(),
    entry: FlowEntryViewSchema,
    exit: FlowExitViewSchema,
});
/**
 * ステージの鎖の中の置き場。鎖は読取がステージの入口の参照から導いたもので、画面は数え直さない。
 *
 * `position` は鎖の中の順（1 から）で、鎖に入らない横断ステージは null。`prev` は前のステージで、
 * 鎖の最初・横断ステージ・前の見つからない入口は null。`next` は出口の結末の行で続く次のステージ
 * （鎖の本線）。
 * @graph-connects none
 */
export const StageChainViewSchema = z.object({
    position: positiveId().nullable(),
    prev: FlowRefSchema.nullable(),
    next: z.array(FlowRefSchema),
});
/**
 * ステージそのもの。`entry` は最初のステージと横断ステージで、`exit` は横断ステージで null。
 * `description` は説明で、表示言語で読めなければ空文字（`descriptionUntranslated` が true）。
 * @graph-connects none
 */
export const StageViewSchema = z.object({
    stageId: positiveId(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    updatedAt: NodeUpdatedAtSchema,
    entry: FlowEndingRowViewSchema.nullable(),
    exit: FlowEndingRowViewSchema.nullable(),
    description: z.string(),
    descriptionUntranslated: UntranslatedSchema,
    chain: StageChainViewSchema,
});
/**
 * `getFlow` の出力。`flowId` で引けば業務フロー 1 本、`stageId` で引けばステージの図
 * （主フロー 1 本 ＋ 支援フローの札）。
 * @graph-connects none
 */
export const GetFlowOutputSchema = z.discriminatedUnion('view', [
    z.object({ view: z.literal('flow'), flow: FlowViewSchema }),
    z.object({
        view: z.literal('stage'),
        stage: StageViewSchema,
        mainFlow: FlowViewSchema.nullable(),
        supportFlows: z.array(SupportFlowCardSchema),
    }),
]);
/**
 * `listPjFlows` の入力。
 * @graph-connects none
 */
export const ListPjFlowsInputSchema = z.object({
    pjNumber: positiveId(),
    ...LocaleInputShape,
});
/**
 * 案件の変更の種類（行の変更前・変更後の有無から導く）。
 * @graph-connects none
 */
export const PJ_FLOW_CHANGE_KINDS = ['added', 'updated', 'deleted'];
/**
 * 案件が変えた業務フロー 1 行。削除の行で型が読めなければ `type` は null。
 * @graph-connects none
 */
export const PjFlowRowSchema = z.object({
    flowId: positiveId(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    type: z.enum(FLOW_TYPES).nullable(),
    stage: FlowRefSchema.nullable(),
    change: z.enum(PJ_FLOW_CHANGE_KINDS),
});
/**
 * `listPjFlows` の出力。
 * @graph-connects none
 */
export const ListPjFlowsOutputSchema = z.object({
    flows: z.array(PjFlowRowSchema),
});
//# sourceMappingURL=user-story-flow.js.map